import { collabStateManager } from '@databases/collab-state-manager';
import { CollabManager } from '@databases/collab-state-manager/collab-manager';
import {
  convertToDatabase,
  convertToDatabaseFromDiff,
  type YJSDatabaseSubset
} from '@databases/collab/convert/to-database';
import { type DefaultValueConverters } from '@databases/collab/default-value-converters';
import { YDatabase } from '@databases/collab/y-database';
import { getActivationId, getCloudId, getVersionId } from '@databases/database-state';
import { currentBridgeData } from '@databases/database-state/state/bridge-data';
import { installPerfTracerForDatabase } from '@databases/frontend-metrics/perf-tracing';
import { logError } from '@databases/logs';
import { UserType } from '@databases/model/value';
import { getService } from '@databases/service-container';
import { type Content } from '@databases/types/confluence';
import { type Database } from '@databases/types/database';
import { getBasicCollabDatabaseFromContent } from '@databases/utils/database/service';
import { applyDatabaseDiff } from '@databases/utils/redux/databases/actions';
import { type CommonRootState } from '@databases/utils/redux/redux';
import { generateId } from '@databases/utils/utils';
import { type AnyAction } from 'redux';
import { type ThunkAction, type ThunkDispatch } from 'redux-thunk';
import type * as Y from 'yjs';

import {
  currentHostFrameContext,
  getFrameContextReferenceId
} from '@canvas/canvas-state/host-api';
import { getExperimentValue } from '@canvas/client-feature-flags';
import { manuallyFireExposureEvent } from '@canvas/client-feature-flags/experiments';

type Dispatcher<Result> = ThunkAction<Result, CommonRootState, void, AnyAction>;

interface DeferredDatabaseProjection {
  entryIds: ReadonlySet<string>;
  deferEntriesOrdering: boolean;
  fieldIds: ReadonlySet<string>;
  deferFieldsOrdering: boolean;
}

interface DeferDatabaseEntryProjectionOptions {
  deferEntriesOrdering?: boolean;
}

interface DeferredFieldProjectionMetadata {
  deferFieldsOrdering: boolean;
  fieldIds: readonly string[];
}

const DEFERRED_FIELD_PROJECTION_META_KEY = Symbol.for(
  'databases:deferred-field-projection'
);

const deferredProjectionByTransaction = new WeakMap<
  Y.Transaction,
  DeferredDatabaseProjection
>();

export function deferDatabaseEntryProjection(
  transaction: Y.Transaction,
  entryIds: readonly string[],
  options: DeferDatabaseEntryProjectionOptions = {}
): void {
  if (entryIds.length === 0 && !options.deferEntriesOrdering) {
    return;
  }

  const deferredProjection = deferredProjectionByTransaction.get(transaction);
  deferredProjectionByTransaction.set(transaction, {
    entryIds: new Set([...(deferredProjection?.entryIds ?? []), ...entryIds]),
    deferEntriesOrdering:
      (deferredProjection?.deferEntriesOrdering ?? false) ||
      (options.deferEntriesOrdering ?? false),
    fieldIds: deferredProjection?.fieldIds ?? new Set(),
    deferFieldsOrdering: deferredProjection?.deferFieldsOrdering ?? false
  });
}

function getDeferredFieldProjectionMetadata(
  transaction: Y.Transaction
): DeferredFieldProjectionMetadata | undefined {
  const metadata: unknown = transaction.meta?.get(DEFERRED_FIELD_PROJECTION_META_KEY);
  if (
    !metadata ||
    typeof metadata !== 'object' ||
    !('fieldIds' in metadata) ||
    !Array.isArray(metadata.fieldIds) ||
    !metadata.fieldIds.every((fieldId) => typeof fieldId === 'string') ||
    !('deferFieldsOrdering' in metadata) ||
    typeof metadata.deferFieldsOrdering !== 'boolean'
  ) {
    return undefined;
  }

  return {
    deferFieldsOrdering: metadata.deferFieldsOrdering,
    fieldIds: metadata.fieldIds
  };
}

function takeDeferredDatabaseProjection(
  transaction: Y.Transaction
): DeferredDatabaseProjection | undefined {
  const deferredProjection = deferredProjectionByTransaction.get(transaction);
  deferredProjectionByTransaction.delete(transaction);
  const deferredFieldProjection = getDeferredFieldProjectionMetadata(transaction);
  if (!deferredProjection && !deferredFieldProjection) {
    return undefined;
  }

  return {
    entryIds: deferredProjection?.entryIds ?? new Set(),
    deferEntriesOrdering: deferredProjection?.deferEntriesOrdering ?? false,
    fieldIds: new Set(deferredFieldProjection?.fieldIds),
    deferFieldsOrdering: deferredFieldProjection?.deferFieldsOrdering ?? false
  };
}

// TODO no redux dependencies, can move out of redux package
export const buildDefaultValueConverters = (): DefaultValueConverters => {
  return {
    getCurrentUser: () => {
      const currentUserId = currentBridgeData.data.identity.accountId;

      return {
        accountId: currentUserId,
        id: generateId(),
        type: UserType.Referenced
      };
    }
  };
};

export const syncCollabChangesToRedux = (
  collabManager: CollabManager,
  dispatch: ThunkDispatch<CommonRootState, void, AnyAction>,
  options: {
    contentId: string;
    isReadOnly?: boolean;
  }
) => {
  return (
    update: Uint8Array,
    origin: unknown,
    doc: Y.Doc,
    transaction: Y.Transaction
  ) => {
    try {
      const { contentId, isReadOnly } = options;
      const collabDatabase = collabManager.database;
      const diff = collabDatabase.getDiff(transaction);
      const deferredProjection = takeDeferredDatabaseProjection(transaction);

      dispatch(
        applyDatabaseDiff({
          contentId,
          diff: deferredProjection
            ? {
                ...diff,
                fieldsOrderingChanged: deferredProjection.deferFieldsOrdering
                  ? false
                  : diff.fieldsOrderingChanged,
                entriesOrderingChanged: deferredProjection.deferEntriesOrdering
                  ? false
                  : diff.entriesOrderingChanged,
                changedFieldIds: diff.changedFieldIds.filter(
                  (fieldId) => !deferredProjection.fieldIds.has(fieldId)
                ),
                changedEntryIds: diff.changedEntryIds.filter(
                  (entryId) => !deferredProjection.entryIds.has(entryId)
                )
              }
            : diff,
          collabDatabase,
          isReadOnly: isReadOnly ?? collabManager.isReadOnly
        })
      );
      // eslint-disable-next-line canvas/no-swallow-errors-in-catch
    } catch (e: unknown) {
      if (e instanceof Error) {
        // Capture exception and allow the user action to proceed
        logError(`Error applying database diff: ${e.message}`, e);
      }
    }
  };
};

// TODO no redux dependencies, can move out of redux package
export const createCollabManager = ({
  contentId,
  spaceId,
  referenceId,
  initialDatabase,
  cloudId,
  activationId,
  versionId
}: {
  contentId: string;
  spaceId: number;
  referenceId: string;
  initialDatabase?: YDatabase;
  cloudId: string | undefined;
  activationId: string | undefined;
  versionId?: string;
}): CollabManager => {
  if (!cloudId) {
    throw new Error('cloudId is not defined');
  }

  if (!activationId) {
    throw new Error('activationId is not defined');
  }

  const emptyDb =
    initialDatabase || new YDatabase(undefined, buildDefaultValueConverters());
  const isDatabaseBEStreamingEnabled = getExperimentValue(
    'databaseBEStreaming',
    'enabled',
    { fireExperimentExposure: false }
  );
  emptyDb.configureBackendAgentUndoCapture({
    isEnabled: isDatabaseBEStreamingEnabled,
    localAccountId: currentBridgeData.data.identity.accountId,
    onEligibleBackendAgentUpdate: () => manuallyFireExposureEvent('databaseBEStreaming')
  });

  const token =
    getFrameContextReferenceId() === referenceId
      ? currentHostFrameContext.data
      : undefined;

  const collabManager = new CollabManager(
    cloudId,
    activationId,
    spaceId.toString(),
    contentId,
    referenceId,
    emptyDb,
    token,
    versionId,
    getService('PresenceState')
  );

  collabStateManager.setCollabManager(contentId, collabManager);

  return collabManager;
};

export const synchronizeViewOrderingExperiment = (
  collabDatabase: Pick<YDatabase, 'synchronizeViewOrderingMode'>
): void => {
  const enabled = getExperimentValue('databasesViewSpecificOrdering', 'enabled', {
    fireExperimentExposure: false
  });
  collabDatabase.synchronizeViewOrderingMode(enabled ? 'view-specific' : 'global');
};

// TODO no redux dependencies, can move out of redux package
export const buildDatabaseFromCollabAndContent = (
  databaseContent: Content<'database'>,
  collabDatabase: YDatabase,
  isReadOnly: boolean,
  transaction?: Y.Transaction,
  baseDatabase?: YJSDatabaseSubset
) => {
  if (!databaseContent.referenceId) {
    throw new Error('missing referenceId');
  }

  synchronizeViewOrderingExperiment(collabDatabase);

  const base = getBasicCollabDatabaseFromContent(databaseContent, isReadOnly);

  const collabDatabaseAsDatabase =
    transaction && baseDatabase
      ? convertToDatabaseFromDiff(
          collabDatabase.getDiff(transaction),
          collabDatabase,
          baseDatabase
        )
      : convertToDatabase(collabDatabase);

  const database: Database = {
    ...base,
    id: databaseContent.referenceId,
    referenceId: databaseContent.referenceId,

    metaVersion: 0,

    entriesContentId: '', // TODO: null this type
    entriesContentVersion: 0,

    ...collabDatabaseAsDatabase
  };

  return database;
};

export const getOrCreateCollabManager =
  (
    {
      contentId,
      spaceId,
      referenceId,
      isInitialLoad = false
    }: {
      contentId: string;
      spaceId: number;
      referenceId: string;
      isInitialLoad: boolean;
    },
    initialDatabase?: YDatabase
  ): Dispatcher<Promise<CollabManager>> =>
  async (dispatch) => {
    if (isInitialLoad) {
      collabStateManager.closeInactiveSessions(contentId);
    }

    const existingCollabManager = collabStateManager.getCollabManager(contentId);

    if (existingCollabManager) {
      return existingCollabManager;
    }

    if (!spaceId && !referenceId) {
      const error = new Error('Missing required parameters: spaceId and referenceId');

      logError(error);
      throw error;
    }

    const cloudId = getCloudId();
    const activationId = getActivationId();
    const versionId = getVersionId();

    const collabManager = createCollabManager({
      contentId,
      spaceId,
      referenceId,
      initialDatabase,
      cloudId,
      activationId,
      versionId
    });

    collabManager.onPermissionsChanged((isReadOnly) => {
      dispatch(
        applyDatabaseDiff({
          contentId,
          diff: {
            fieldsOrderingChanged: false,
            entriesOrderingChanged: false,
            changedFieldIds: [],
            changedEntryIds: [],
            threadsChanged: false,
            viewsChanged: false,
            defaultEntryChanged: false,
            othersChanged: false
          },
          collabDatabase: collabManager.database,
          isReadOnly
        })
      );
    });

    collabManager.database.doc.on(
      'update',
      syncCollabChangesToRedux(collabManager, dispatch, { contentId })
    );

    // Install after syncCollabChangesToRedux so Yjs invokes this update
    // listener after the Redux dispatch has returned.
    installPerfTracerForDatabase(collabManager, contentId);

    return collabManager;
  };
